import { Component, OnDestroy, OnInit } from '@angular/core';
import { Subject, switchMap, take, takeUntil, tap, timer } from 'rxjs';
import {
    OrganizationProperties,
    UserAuth,
    UserProfile,
} from 'src/app/authentication/profile/models/user-profile';
import { AuthenticationService } from 'src/app/authentication/services/authentication.service';
import { UserService } from 'src/app/authentication/services/user.service';
import { NavigationEnd, Router } from '@angular/router';
import { SidebarNavService } from '../../services/sidebar-nav.service';
import { DynamicConfigService } from 'src/app/core/services/dynamic-config.service';
import { ContainerType } from '../../../../shared/models/container.types';
import {
    SIDEBAR_NAV_ROUTE_DATA,
    SidebarNavRouteData,
} from '../../../../shared/models/sidebar-nav-route-data';
import { FormControl } from '@angular/forms';
import { ToastMessageService } from 'src/app/shared/services/toast-message.service';

@Component({
    selector: 'app-sidebar-nav',
    templateUrl: './sidebar-nav.component.html',
    styleUrls: ['./sidebar-nav.component.scss'],
    standalone: false,
})
export class SidebarNavComponent implements OnInit, OnDestroy {
    isExpanded: boolean = true;
    isUserSectionExpanded: boolean = false;

    activeNavItem: number = 0;
    isAdmin: boolean;
    hasOrganizationFeature: boolean;
    isOrgBound: boolean;
    isInAnyOrg: boolean;
    isInOrgMode: boolean;
    picture: string;
    username: string;
    userRoles: string[];
    homeUrl: string;
    homeUrlWithProtocol: string;

    orgs: { id: string; name: string }[] = [];
    orgControl: FormControl = new FormControl();

    // INFO: to adjust the animation delay for the side bar navigation
    // it need also to adjust the duaration variable in the scss file
    // variable called "$sideBarDuration"
    private readonly ANIMATION_DELAY: number = 600;
    private destroy$: Subject<void> = new Subject<void>();

    constructor(
        private userService: UserService,
        private authenticationService: AuthenticationService,
        private router: Router,
        private sidebarNavService: SidebarNavService,
        private configService: DynamicConfigService,
        private toastService: ToastMessageService
    ) {
        this.homeUrl = this.configService.homeUrl;
        // Always treat homeUrl as absolute domain (without protocol)
        // Build scheme-relative URL so the current protocol is preserved
        this.homeUrlWithProtocol = `//${this.homeUrl}`;
    }

    ngOnInit(): void {
        this.authenticationService.initializationFinished$
            .pipe(
                take(1),
                switchMap(() =>
                    this.authenticationService
                        .observableUserAuth()
                        .pipe(takeUntil(this.destroy$))
                ),
                tap((userAuth: UserAuth) => {
                    this.username = userAuth.username;
                    this.isAdmin = this.authenticationService.isAdmin();
                    this.hasOrganizationFeature =
                        this.authenticationService.hasOrganizationFeatureAccess();
                    this.isInAnyOrg =
                        Object.keys(userAuth.organizationProperties).length > 0;
                    this.isOrgBound =
                        this.authenticationService.isUserBoundToAnyOrg();
                    if (this.isInAnyOrg) {
                        const organizationGroupMap =
                            userAuth.organizationProperties ?? {};

                        let orgs = Object.entries(organizationGroupMap).map(
                            ([id, organizationProperties]: [
                                string,
                                OrganizationProperties,
                            ]): { id: string; name: string } => ({
                                id,
                                name: organizationProperties.name,
                            })
                        );
                        if (!this.isOrgBound) {
                            orgs.unshift({
                                id: '',
                                name: 'navigation.sidebarNav.noOrg',
                            });
                        }
                        this.orgs = orgs;

                        const activeOrganization =
                            userAuth.activeOrganization ?? '';
                        this.isInOrgMode = !!activeOrganization;

                        this.orgControl.setValue(activeOrganization, {
                            emitEvent: false,
                        });
                    }
                    this.userRoles = userAuth.roles;
                }),
                switchMap(() => {
                    return this.userService.profilePicture$.pipe(
                        takeUntil(this.destroy$)
                    );
                }),
                tap((picture: string) => {
                    this.picture = picture;
                })
            )
            .subscribe();

        this.router.events.pipe(takeUntil(this.destroy$)).subscribe({
            next: (event) => {
                if (event instanceof NavigationEnd) {
                    this.setNavItemSelectionBasedOnUrl(event.url);
                }
            },
        });
        this.setNavItemSelectionBasedOnUrl(
            this.getPath().replace('/tt/fe', '')
        );
    }

    private getPath(): string {
        return location.pathname;
    }

    private setNavItemSelectionBasedOnUrl(_url: string) {
        const selectedNavItem = this.getNavItemFromCurrentRoute();

        if (selectedNavItem !== null) {
            this.activeNavItem = selectedNavItem;
        }
    }

    private getNavItemFromCurrentRoute(): number | null {
        let routeSnapshot = this.router.routerState.snapshot.root;

        while (routeSnapshot.firstChild) {
            routeSnapshot = routeSnapshot.firstChild;
        }

        const sidebarNav = routeSnapshot.data[
            'sidebarNav'
        ] as SidebarNavRouteData;

        switch (sidebarNav) {
            case SIDEBAR_NAV_ROUTE_DATA.WORKSPACE:
                return 0;
            case SIDEBAR_NAV_ROUTE_DATA.COMMUNITY_PROJECTS:
                return 4;
            case SIDEBAR_NAV_ROUTE_DATA.PROFILE:
                return 5;
            case SIDEBAR_NAV_ROUTE_DATA.ORGANIZATION:
                return 6;
            case SIDEBAR_NAV_ROUTE_DATA.ADMIN:
                return 7;
            case SIDEBAR_NAV_ROUTE_DATA.WORKSPACE_BY_TYPE:
                return this.getWorkspaceTypeNavItem(
                    routeSnapshot.params['type']
                );
            case SIDEBAR_NAV_ROUTE_DATA.PROJECT_BY_CONTAINER_TYPE:
            case SIDEBAR_NAV_ROUTE_DATA.DB_BY_CONTAINER_TYPE:
                return this.getContainerTypeNavItem(
                    routeSnapshot.params['containerType']
                );
            default:
                return null;
        }
    }

    private getWorkspaceTypeNavItem(type: string): number | null {
        switch (type) {
            case 'motoric':
                return 1;
            case 'visual':
                return 2;
            case 'imageDb':
                return 3;
            default:
                return null;
        }
    }

    private getContainerTypeNavItem(containerType: string): number | null {
        switch (containerType) {
            case ContainerType.PROJECT_MOTORIC:
                return 1;
            case ContainerType.PROJECT_VISUAL:
                return 2;
            case ContainerType.IMAGE_DB:
                return 3;
            default:
                return null;
        }
    }

    trackOrgById(_index: number, org: { id: string; name: string }): string {
        return org.id;
    }

    getSelectedOrgName() {
        return this.orgs.find(
            (orgOption) => orgOption.id === this.orgControl.value
        )?.name;
    }

    onExpand() {
        this.isExpanded = !this.isExpanded;
        this.isUserSectionExpanded = false;
        timer(this.ANIMATION_DELAY + 10)
            .pipe(take(1))
            .subscribe(() =>
                this.sidebarNavService.sidebarNavExpansion$.next()
            );
    }

    ngOnDestroy(): void {
        this.destroy$.complete();
    }

    onUserSectionExpand() {
        if (this.isExpanded) {
            this.isUserSectionExpanded = !this.isUserSectionExpanded;
        }
    }

    onLogOut() {
        this.authenticationService.signOut();
    }

    onOrgChange(orgChange: Event) {
        const orgId = (orgChange.target as HTMLSelectElement).value;
        const previousValue =
            this.authenticationService.currentUserValue.activeOrganization ??
            '';
        this.userService
            .updateUser(
                {
                    activeOrganization: orgId,
                    activeOrganizationGroup: '',
                } as UserProfile,
                this.authenticationService.currentUserValue.id
            )
            .pipe(switchMap(() => this.authenticationService.refreshUserAuth()))
            .subscribe({
                next: () => {
                    this.toastService.showTranslatedSuccessMsg(
                        'navigation.sidebarNav.success.contextSwitch',
                        {},
                        null
                    );
                },
                error: () => {
                    this.orgControl.setValue(previousValue);
                    this.toastService.showTranslatedErrorMsg(
                        'navigation.sidebarNav.error.contextSwitch',
                        {},
                        null
                    );
                },
            });
    }
}
